import { observer } from 'mobx-react'
import { Alert, AlertIcon, AlertDescription, Button, HStack, CloseButton } from '@chakra-ui/react'
import { useState, useCallback } from 'react'
import { useStores } from 'hooks/useStores'
import { BillingStatus } from 'types/billing'
import { createBillingPortalSession } from 'API/billing'
import { toast } from 'react-toastify'

/**
 * Banner shown at the top of the layout when a membership has billing issues.
 * OVERDUE = warning (within grace period), LOCKED = error (access blocked).
 */
const BillingStatusBanner = () => {
  const { authStore } = useStores()
  const { currentMembership } = authStore
  const [dismissed, setDismissed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const billingStatus = currentMembership?.billingStatus

  const handleUpdatePayment = useCallback(async () => {
    setIsLoading(true)
    try {
      const returnUrl = window.location.href
      const { url } = await createBillingPortalSession(returnUrl)
      window.location.href = url
    } catch {
      toast.error('Unable to open billing portal. Please contact support.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Only show for OVERDUE or LOCKED
  if (!billingStatus || dismissed) return null
  if (billingStatus !== BillingStatus.OVERDUE && billingStatus !== BillingStatus.LOCKED) return null

  const isLocked = billingStatus === BillingStatus.LOCKED
  const status = isLocked ? 'error' : 'warning'
  const message = isLocked
    ? 'Your access is suspended due to unpaid billing. Please update your payment method to restore access.'
    : 'Your payment is overdue. Please update your payment method to avoid access suspension.'

  return (
    <Alert status={status} variant="solid" borderRadius={0}>
      <AlertIcon />
      <AlertDescription flex={1}>{message}</AlertDescription>
      <HStack spacing={2}>
        <Button
          size="sm"
          variant="outline"
          colorScheme="whiteAlpha"
          color="white"
          isLoading={isLoading}
          onClick={handleUpdatePayment}
        >
          Update Payment
        </Button>
        {!isLocked && (
          <CloseButton onClick={() => setDismissed(true)} />
        )}
      </HStack>
    </Alert>
  )
}

export default observer(BillingStatusBanner)
